import { FlexColumn, LabeledInput, useLoading } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { useEffect } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES, REQUIRED_FIELD_TEXT } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useForms } from '../../../hooks/useForms';
import { useStyles } from '../../../hooks/useStyles';
import { createJobSkill, updateJobSkill } from '../../../services/jobsService';
import {
  editSkillErrorState,
  editSkillState,
  editSkillTouchedState,
  editSkillValidState,
} from '../../../storage/jobSkillsStates';
import { Skill, SkillRequestTouched } from '../../../types/jobs';
import { FormActionsRow } from '../../shared/FormActionsRow';
import StyledModal from '../../shared/StyledModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  skillSlug?: string;
  skill?: Skill;
  onSubmitFinished?: () => void;
};

export function SkillModal({ isOpen, onClose, skillSlug, skill, onSubmitFinished }: Readonly<Props>) {
  const editMode = skillSlug !== undefined && skill !== undefined;
  const { css } = useStyles();
  const [form, setForm] = useRecoilState(editSkillState);
  const [formTouched, setFormTouched] = useRecoilState(editSkillTouchedState);
  const formHasError = useRecoilValue(editSkillErrorState);
  const resetEditSkillState = useResetRecoilState(editSkillState);
  const resetEditSkillTouchedState = useResetRecoilState(editSkillTouchedState);
  const formValid = useRecoilValue(editSkillValidState);
  const { loading, startLoading, stopLoading } = useLoading();
  const canSubmit = !formHasError;
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const { onTouched } = useForms<Skill, SkillRequestTouched>(setForm, setFormTouched);

  useEffect(() => {
    if (isOpen) {
      return clearStates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!skill) return;
    setForm(skill);
  }, [skill]);

  const clearStates = () => {
    resetEditSkillState();
    resetEditSkillTouchedState();
  };

  const onChangeInput = (key: keyof Skill, value: any) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onSubmit = async () => {
    const onSuccess = (successMessage: string) => {
      showPositiveFeedback(successMessage);
      clearStates();
      onSubmitFinished && onSubmitFinished();
    };
    startLoading();
    if (editMode) {
      const { error } = await updateJobSkill(skillSlug, form);
      if (!error) {
        onSuccess(FEEDBACK.edited(FEEDBACK_PREFIXES.jobSkill, form.name));
      } else {
        showFailFeedback(error);
      }
    } else {
      const { error } = await createJobSkill(form);
      if (!error) {
        onSuccess(FEEDBACK.created(FEEDBACK_PREFIXES.jobSkill));
      } else {
        showFailFeedback(error);
      }
    }
    stopLoading();
  };

  const onCloseModal = () => {
    clearStates();
    onClose();
  };

  return (
    <StyledModal
      isOpen={isOpen}
      closeable
      onClose={onCloseModal}
      size="auto"
      title={editMode ? 'Editar ' : 'Nueva '}
      content={
        <FlexColumn
          classNames={css({
            justifyContent: 'space-between',
            overflow: 'hidden',
            minWidth: '525px',
          })}
        >
          <FlexColumn
            classNames={css({
              overflow: 'auto',
              height: '100%',
            })}
          >
            <LabeledInput
              label="Nombre"
              placeholder="Ingresa nombre de la habilidad"
              value={form.name}
              onChange={(value) => {
                onChangeInput('name', value);
              }}
              onBlur={() => onTouched('nameTouched')}
              error={formTouched.nameTouched && !formValid.name && REQUIRED_FIELD_TEXT}
              required
            />
            <LabeledInput
              label="Slug"
              placeholder="Ingresa el slug"
              value={form.slug}
              onChange={(value) => {
                onChangeInput('slug', value);
              }}
              onBlur={() => onTouched('slugTouched')}
              error={
                formTouched.slugTouched &&
                !formValid.slug &&
                (form.slug ? 'Ingrese un slug valido' : REQUIRED_FIELD_TEXT)
              }
              required
            />
            {editMode && (
              <Checkbox
                checked={form.active}
                onChange={(e) => onChangeInput('active', e.target.checked)}
              >
                Activo
              </Checkbox>
            )}
          </FlexColumn>
          <FormActionsRow
            canSubmit={canSubmit}
            loading={loading}
            editMode={editMode}
            onSubmit={onSubmit}
            onCancel={onCloseModal}
          />
        </FlexColumn>
      }
    />
  );
}
